import { useEffect, useMemo } from 'react';
import { Box, Center, Field, Input, SimpleGrid, Spinner, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
  Button as ButtonComponent,
  DataTable,
  DialogComponent,
  Pagination,
  StatusBadge,
  TableOptions,
  type Column,
  type ToggleOption,
} from '@themes/aegix';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { DrawerComponent } from '../ui/drawerComponent';
import { useCanManageOrganizations } from '../../hooks/useCanManageOrganizations';
import OrganizationsForm from '../forms/OrganizationsForm';
import {
  organizationFormValidationSchema,
  type OrganizationFormValues,
} from '../../validations/organization.validation';
import {
  useOrganizationsContentData,
  type OrganizationRow,
} from './hooks/useOrganizationsContentData';

export function OrganizationsContent() {
  const { t } = useTranslation();
  const {
    drawerOpen,
    currentRow,
    statusFilter,
    setStatusFilter,
    searchValue,
    setSearchValue,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    actionMode,
    sortColumn,
    sortDirection,
    filterValues,
    drawerContentRef,
    deleteDialogOpen,
    deleteConfirmText,
    setDeleteConfirmText,
    isDeleting,
    filteredOrganizations,
    totalItems,
    totalPages,
    isLoading,
    error,
    organizationFormDefaultValues,
    handleSort,
    handleFilterChange,
    handleFilterClear,
    handleDrawerClose,
    handleSubmit,
    openAddDrawer,
    openEditDrawer,
    openDeleteDialog,
    closeDeleteDialog,
    handleDelete,
  } = useOrganizationsContentData();

  const statusToggleOptions: ToggleOption[] = useMemo(
    () => [
      { value: 'all', label: t('common.all', 'All') },
      { value: 'onboard', label: t('org.statusOnboard', 'Onboard') },
      { value: 'online', label: t('org.statusOnline', 'Online') },
      { value: 'offline', label: t('org.statusOffline', 'Offline') },
    ],
    [t],
  );

  const columns: Column<OrganizationRow>[] = useMemo(
    () => [
      { key: 'name', label: t('common.name', 'Name'), sortable: true, filterable: true, width: '260px' },
      { key: 'domain', label: t('common.domain', 'Domain'), sortable: true, filterable: true, width: '220px' },
      { key: 'industry', label: t('common.industry', 'Industry'), sortable: true, filterable: true, width: '160px' },
      { key: 'type', label: t('common.type', 'Type'), sortable: true, filterable: true, width: '140px' },
      {
        key: 'status',
        label: t('common.status', 'Status'),
        sortable: true,
        filterable: true,
        filterOptions: [
          { label: t('org.statusOnboard', 'Onboard'), value: 'onboard' },
          { label: t('org.statusOnline', 'Online'), value: 'online' },
          { label: t('org.statusOffline', 'Offline'), value: 'offline' },
        ],
        width: '120px',
        render: (row) => (
          <StatusBadge
            status={row.status}
            label={t(`org.status${row.status.charAt(0).toUpperCase()}${row.status.slice(1)}`, row.status)}
          />
        ),
      },
    ],
    [t],
  );

  const canManageOrganizations = useCanManageOrganizations();
  const canDelete = actionMode === 'edit' && canManageOrganizations;

  const formMethods = useForm<OrganizationFormValues>({
    defaultValues: organizationFormDefaultValues,
    resolver: zodResolver(organizationFormValidationSchema),
    mode: 'onSubmit',
  });

  useEffect(() => {
    formMethods.reset(organizationFormDefaultValues);
  }, [organizationFormDefaultValues, formMethods]);

  return (
    <>
      <TableOptions
        toggleOptions={statusToggleOptions}
        activeToggle={statusFilter}
        onToggleChange={(v: string) => {
          setStatusFilter(v as 'all' | 'onboard' | 'online' | 'offline');
          setCurrentPage(1);
        }}
        searchValue={searchValue}
        onSearchChange={(v) => {
          setSearchValue(v);
          setCurrentPage(1);
        }}
        searchPlaceholder={t('common.searchPlaceholder', 'Search...')}
        primaryAction={{
          label: t('org.addOrg', 'Add Organization'),
          onClick: openAddDrawer,
        }}
      />

      <Box px={5} pb={5}>
        {isLoading ? (
          <Center py={12}>
            <Spinner size="lg" color="button.primary" />
          </Center>
        ) : error ? (
          <Center py={12}>
            <Text color="text.danger">
              {error instanceof Error
                ? error.message
                : t('org.failedLoad', 'Failed to load organizations')}
            </Text>
          </Center>
        ) : filteredOrganizations.length === 0 ? (
          <Center py={12}>
            <Text color="text.secondary">{t('org.noOrgs', 'No organizations found')}</Text>
          </Center>
        ) : (
          <>
            <DataTable
              columns={columns}
              data={filteredOrganizations}
              keyExtractor={(row) => String(row.id)}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
              filterValues={filterValues}
              onFilterChange={handleFilterChange}
              onFilterClear={handleFilterClear}
              onRowClick={(row) => openEditDrawer(row)}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
            />
          </>
        )}

        <DrawerComponent
          isOpen={drawerOpen}
          onClose={() => handleDrawerClose()}
          title={
            actionMode === 'add' ? t('org.addNewOrg', 'Add New Organization') : t('org.editOrg', 'Edit Organization')
          }
          size="lg"
          closeOnOutside={false}
          closeOnEsc={false}
          drawerContentRef={drawerContentRef}
          footer={
            <SimpleGrid columns={canDelete ? 3 : 2} gap={4} width="100%">
              {canDelete && (
                <ButtonComponent
                  visual="alert"
                  type="button"
                  onClick={openDeleteDialog}
                  data-e2e="delete-organization-button"
                >
                  {t('common.delete', 'Delete')}
                </ButtonComponent>
              )}
              <ButtonComponent
                visual="secondary"
                type="button"
                onClick={() => handleDrawerClose()}
              >
                {t('common.cancel', 'Cancel')}
              </ButtonComponent>
              <ButtonComponent
                visual="primary"
                type="submit"
                form="organizations-form"
              >
                {t('common.save', 'Save')}
              </ButtonComponent>
            </SimpleGrid>
          }
        >
          <FormProvider {...formMethods}>
            <form
              id="organizations-form"
              onSubmit={formMethods.handleSubmit((values) =>
                handleSubmit(values),
              )}
            >
              <OrganizationsForm currentRow={currentRow} />
            </form>
          </FormProvider>
        </DrawerComponent>

        <DialogComponent
          e2eTitle="deleteOrganization"
          isOpen={deleteDialogOpen}
          onClose={closeDeleteDialog}
          headingText={t('org.deleteOrgHeading', 'Delete Organization')}
          size="md"
        >
          <Box>
            <Text color="text.secondary" mb={4}>
              {t(
                'org.deleteConfirmNotice',
                'Deleting {{name}} archives the organization and all of its schools/sites — they’re removed from the system but not erased, and an Aegix admin can restore them. Visitor and reunification records are kept with the archived organization.',
                { name: currentRow?.name ?? '' },
              )}
            </Text>
            <Field.Root gap={0}>
              <Field.Label mb={2} fontWeight="medium">
                {t('org.typeConfirmLabel', 'Type the organization name to confirm:')}
              </Field.Label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder={currentRow?.name ?? ''}
                autoComplete="off"
                data-e2e="delete-organization-confirm-input"
              />
            </Field.Root>
            <SimpleGrid columns={2} gap={4} width="100%" mt={4}>
              <ButtonComponent
                visual="secondary"
                type="button"
                onClick={closeDeleteDialog}
              >
                {t('common.cancel', 'Cancel')}
              </ButtonComponent>
              <ButtonComponent
                visual="alert"
                type="button"
                onClick={() => handleDelete()}
                disabled={
                  isDeleting ||
                  deleteConfirmText.trim() !== (currentRow?.name ?? '')
                }
                data-e2e="confirm-delete-organization-button"
              >
                {isDeleting ? t('common.deleting', 'Deleting…') : t('org.deleteOrg', 'Delete Organization')}
              </ButtonComponent>
            </SimpleGrid>
          </Box>
        </DialogComponent>
      </Box>
    </>
  );
}
